import { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { SEO } from "@/components/SEO"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { visibleBlogPosts, getBlogPostBySlug, getPrevBlogPost, getNextBlogPost, formatBlogDate } from "@/config/blog"
import { ChevronLeft, ChevronRight, Printer } from "lucide-react"
import { BlogDisclaimer } from "@/components/BlogDisclaimer"

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [content, setContent] = useState<string>("")
  const [loading, setLoading] = useState(true)

  // Get current post from URL slug or default to first post
  const currentPost = slug ? getBlogPostBySlug(slug) : visibleBlogPosts[0]
  const actualPost = currentPost || visibleBlogPosts[0]

  // Get previous and next posts for navigation
  const prevPost = getPrevBlogPost(actualPost)
  const nextPost = getNextBlogPost(actualPost)

  useEffect(() => {
    const fetchMarkdown = async () => {
      setLoading(true)
      try {
        const response = await fetch(actualPost.path)
        if (!response.ok) {
          throw new Error(`Failed to fetch ${actualPost.path}`)
        }
        const text = await response.text()
        setContent(text)
      } catch (error) {
        console.error("Error loading markdown:", error)
        setContent("# Error\n\nFailed to load blog post.")
      } finally {
        setLoading(false)
      }
    }

    fetchMarkdown()
  }, [actualPost.path])

  // Redirect to blog list if slug not found
  useEffect(() => {
    if (slug && !currentPost) {
      navigate("/blog", { replace: true })
    }
  }, [slug, currentPost, navigate])

  return (
    <>
      <SEO
        title={actualPost.title}
        url={`/blog/${actualPost.slug}`}
        description={actualPost.description}
        keywords={`${actualPost.title.toLowerCase()}, WELEPHANT blog, prediction mining, blockchain gaming`}
      />
      <div className="container py-8">
        <div className="max-w-3xl mx-auto">
          {/* Back to Blog */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Blog
          </Link>

          {/* Content */}
          <Card className="overflow-hidden">
            {/* Cover Image */}
            {actualPost.coverImage && (
              <div className="aspect-video w-full overflow-hidden bg-muted">
                <img
                  src={actualPost.coverImage}
                  alt={actualPost.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-6 md:p-8">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="text-muted-foreground">Loading...</div>
                </div>
              ) : (
                <>
                  {/* Post Meta */}
                  <div className="mb-6 pb-6 border-b">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <time dateTime={actualPost.date}>{formatBlogDate(actualPost.date)}</time>
                      <span className="text-border">|</span>
                      <span>{actualPost.author}</span>
                    </div>
                  </div>

                  <article className="print-content prose prose-neutral dark:prose-invert max-w-full [overflow-wrap:anywhere]">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => (
                        <div className="flex items-start justify-between gap-2 mb-4">
                          <h1 className="text-3xl font-bold tracking-tight">
                            {children}
                          </h1>
                          <Button variant="ghost" size="icon" className="print:hidden shrink-0 mt-1" onClick={() => window.print()}>
                            <Printer className="h-4 w-4" />
                          </Button>
                        </div>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-2xl font-semibold mt-8 mb-4 border-b pb-2">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-xl font-semibold mt-6 mb-3">
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p className="text-muted-foreground leading-7 mb-4">
                          {children}
                        </p>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal list-inside space-y-2 text-muted-foreground mb-4">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-muted-foreground">{children}</li>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground my-4">
                          {children}
                        </blockquote>
                      ),
                      code: ({ className, children }) => {
                        const isInline = !className
                        return isInline ? (
                          <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">
                            {children}
                          </code>
                        ) : (
                          <code className="block bg-muted p-4 rounded-lg text-sm font-mono overflow-x-auto my-4">
                            {children}
                          </code>
                        )
                      },
                      pre: ({ children }) => (
                        <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4">
                          {children}
                        </pre>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto my-4 max-w-full">
                          <table className="min-w-full border-collapse">
                            {children}
                          </table>
                        </div>
                      ),
                      thead: ({ children }) => (
                        <thead className="bg-muted">{children}</thead>
                      ),
                      th: ({ children }) => (
                        <th className="border px-4 py-2 text-left font-semibold">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="border px-4 py-2 text-muted-foreground">
                          {children}
                        </td>
                      ),
                      hr: () => <hr className="my-8 border-border" />,
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          className="text-primary hover:underline"
                          target={href?.startsWith("http") ? "_blank" : undefined}
                          rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
                        >
                          {children}
                        </a>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold text-foreground">
                          {children}
                        </strong>
                      ),
                      img: ({ src, alt }) => (
                        <img
                          src={src}
                          alt={alt || ""}
                          className="max-w-full h-auto rounded-lg my-4"
                        />
                      ),
                    }}
                  >
                    {content}
                  </ReactMarkdown>
                </article>

                  <BlogDisclaimer />
              </>
            )}

              {/* Prev / Next navigation */}
              {!loading && (
                <nav className="flex justify-between mt-8 pt-6 border-t">
                  <div>
                    {prevPost && (
                      <Button
                        variant="ghost"
                        asChild
                      >
                        <Link to={`/blog/${prevPost.slug}`}>
                          <ChevronLeft />
                          {prevPost.title}
                        </Link>
                      </Button>
                    )}
                  </div>
                  <div>
                    {nextPost && (
                      <Button
                        variant="ghost"
                        asChild
                      >
                        <Link to={`/blog/${nextPost.slug}`}>
                          {nextPost.title}
                          <ChevronRight />
                        </Link>
                      </Button>
                    )}
                  </div>
                </nav>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}
